/**
 * The signed-in identity, exactly as `GET /api/session/me` reports it
 * (`Rws.AiInsights.Host.Session.SessionPerson`).
 */
import { apiFetch } from "./apiFetch";
export interface SessionPerson {
  /** The person record's identifier, or `null` when the portal holds no record for this identity. */
  id: string | null;
  /** The organisational email address the identity signed in with. */
  email: string;
  /** The display name held on the person record, or an empty string when there is no record. */
  displayName: string;
}

/** How far a signed-in identity has got through classification (`UR-V-01`, `CON-25`). */
export type ClassificationState = "Unknown" | "Pending" | "Active" | "Inactive";

/** The body `GET /api/session/me` returns for an authenticated caller. */
export interface SessionMeResponse {
  person: SessionPerson;
  classificationState: ClassificationState;
  permissions: string[];
  /** True while this session runs as a person an administrator impersonated. Absent from older responses. */
  impersonated?: boolean;
}

/** One summary row from the impersonation search result. */
export interface PersonSummary {
  id: string;
  email: string;
  displayName: string;
  status: string;
  createdAt: string;
}

/** One page of impersonation candidates. */
export interface PersonListPage {
  items: PersonSummary[];
  totalCount: number;
}

/**
 * The outcome of asking the backend-for-frontend who, if anyone, is signed in. Modelled as a
 * discriminated union rather than a nullable object so a caller cannot forget to check the
 * `kind` before reading a field that is only meaningful for one outcome.
 */
export type SessionQueryResult =
  | { kind: "signed-out" }
  | { kind: "authenticated"; response: SessionMeResponse }
  | { kind: "error"; message: string };

const sessionMePath = "/api/session/me";
const sessionLoginPath = "/api/session/login";
const sessionLogoutPath = "/api/session/logout";
const impersonationSearchPath = "/api/session/impersonate/people";
const impersonationPath = "/api/session/impersonate";
const revertImpersonationPath = "/api/session/impersonate/revert";

/**
 * Calls `GET /api/session/me` and interprets the result. A `401` is not an error — it is the
 * expected answer for a browser with no session cookie yet — so it is reported as
 * `{ kind: "signed-out" }` rather than as `{ kind: "error" }`.
 *
 * @returns The signed-out, authenticated or error outcome of the query.
 */
export async function fetchSession(): Promise<SessionQueryResult> {
  try {
    const response = await fetch(sessionMePath, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (response.status === 401) {
      return { kind: "signed-out" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The session endpoint returned ${String(response.status)}.` };
    }

    const body = (await response.json()) as SessionMeResponse;
    return { kind: "authenticated", response: body };
  } catch {
    // A network failure (the API is unreachable) is exactly the case QR-07 requires the portal to
    // stay usable through — the shell renders an error state rather than crashing.
    return { kind: "error", message: "The session endpoint could not be reached." };
  }
}

/**
 * Signs the current session out. Idempotent on the server, so it is safe to call even when the
 * caller believes there is no active session.
 */
export async function signOut(): Promise<void> {
  await apiFetch(sessionLogoutPath, {
    method: "POST",
    credentials: "same-origin",
    cache: "no-store",
  });
}

/** Searches for a person the current user may impersonate. */
export async function searchImpersonationTargets(search: string): Promise<{ kind: "ok"; response: PersonListPage } | { kind: "denied" } | { kind: "error"; message: string }> {
  const params = new URLSearchParams({ offset: "0", pageSize: "10" });
  if (search.trim().length > 0) {
    params.set("search", search.trim());
  }

  try {
    const response = await apiFetch(`${impersonationSearchPath}?${params.toString()}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The impersonation search endpoint returned ${String(response.status)}.` };
    }

    const body = (await response.json()) as PersonListPage;
    return { kind: "ok", response: body };
  } catch {
    return { kind: "error", message: "The impersonation search endpoint could not be reached." };
  }
}

/** Ends an impersonation and restores the identity that started it. */
export async function revertImpersonation(): Promise<{ kind: "ok" } | { kind: "error"; message: string }> {
  try {
    const response = await apiFetch(revertImpersonationPath, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      return { kind: "error", message: `The revert endpoint returned ${String(response.status)}.` };
    }

    return { kind: "ok" };
  } catch {
    return { kind: "error", message: "The revert endpoint could not be reached." };
  }
}

/** Switches the active browser session to the chosen person's identity. */
export async function impersonateUser(personId: string): Promise<{ kind: "ok" } | { kind: "denied" } | { kind: "error"; message: string }> {
  try {
    const response = await apiFetch(impersonationPath, {
      method: "POST",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ personId }),
      cache: "no-store",
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The impersonation endpoint returned ${String(response.status)}.` };
    }

    return { kind: "ok" };
  } catch {
    return { kind: "error", message: "The impersonation endpoint could not be reached." };
  }
}

/**
 * Builds the URL that starts sign-in, carrying the current location back through the OpenID
 * Connect round trip so the user lands where they were rather than always at the root.
 *
 * This is a real navigation target (an `<a href>`), not something to `fetch` — signing in means
 * following a server-issued redirect to the identity provider, which only a full page navigation
 * can do.
 *
 * @param returnPath - The local path to return to once sign-in completes.
 * @returns The absolute path to navigate the browser to in order to sign in.
 */
export function buildSignInUrl(returnPath: string): string {
  return `${sessionLoginPath}?returnUrl=${encodeURIComponent(returnPath)}`;
}

/**
 * Starts sign-in only once the portal is known to be reachable. Sign-in is a full navigation to the server, which
 * answers with a redirect to the identity provider; when the server is down, that navigation would leave the person on
 * the browser's own error page instead of the portal. So the session endpoint is asked first: any answer below 500
 * (a `401` included) means the server is up, and the browser goes on to sign in. A redirect cannot be fetched and
 * followed from here instead: the identity provider is another origin, so the browser does not hand the page its address.
 *
 * @param returnPath - The local path to return to once sign-in completes.
 * @returns False when the portal could not be reached, so the caller can say so; otherwise the browser is leaving.
 */
export async function signInWhenReachable(returnPath: string): Promise<boolean> {
  try {
    const response = await fetch(sessionMePath, { method: "GET", credentials: "same-origin", headers: { Accept: "application/json" }, cache: "no-store" });
    if (response.status >= 500) {
      return false;
    }
  } catch {
    return false;
  }

  redirectToSignIn(returnPath);
  return true;
}

/**
 * Sends the whole browser to the identity provider, returning to `returnPath` afterwards. A full
 * navigation, because the server answers with the redirect to the identity provider.
 *
 * @param returnPath - The local path to return to once sign-in completes.
 */
export function redirectToSignIn(returnPath: string): void {
  window.location.assign(buildSignInUrl(returnPath));
}
